import { type UserResponse } from 'stream-chat';
import { customRenderText } from './custom-render-text';
import { renderLiteralText } from './render-literal-text';

/**
 * Stream user role the Flow backend gives every client (patient); psychologists
 * get `online_psychologist` or `consultation_room_psychologist` and the
 * automated iPractice user gets `admin`.
 */
const CLIENT_ROLE = 'client';

/**
 * Picks how a message's text should be rendered, from who wrote it.
 *
 * Chat text is markdown on the wire, but only one side can produce it on
 * purpose: psychologists compose in the Dashboard's rich-text editor, while
 * this app offers a plain textarea. Parsing a client's own prose as markdown
 * therefore only ever deletes characters they meant to send (IP-2192).
 *
 * The role is read off the message's own author rather than from channel
 * members, so it is the same field, from the same response, that the Dashboard
 * reads for the same message — the two views cannot disagree.
 *
 * Tested against `role !== 'client'` rather than an allow-list of staff roles:
 * a role we do not recognise is far more likely to be a new kind of
 * practitioner than a client, and rendering a practitioner's markdown as
 * literal text is the milder failure. This matches the backend's own
 * "unknown role is a practitioner" default.
 *
 * The connected user of this app is always a client, so their own id is also
 * accepted. That is deliberate belt and braces: an optimistic outgoing message
 * takes its author from `client.user`, and stream-chat drops any key missing
 * from a `user.updated` payload unless it is in its own-user allowlist, which
 * `role` is not — so a client's own message could otherwise come back through
 * the markdown renderer.
 */
export const pickMessageRenderer = (author?: UserResponse, ownUserId?: string) =>
  author?.role === CLIENT_ROLE || (!!ownUserId && author?.id === ownUserId)
    ? renderLiteralText
    : customRenderText;
